import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { testimonialsApi } from '../../api/testimonials';
import { queryKeys } from '../queryClient';
import type { TestimonialInput } from '../../types/domain';

export function useTestimonials() {
  return useQuery({ queryKey: queryKeys.testimonials, queryFn: () => testimonialsApi.list() });
}

/** Public, anonymous, published-only testimonials — for storefront display. `featured: true`
 * returns just the admin-curated home-page selection. */
export function usePublicTestimonials(featured?: boolean) {
  return useQuery({
    queryKey: queryKeys.testimonialsPublic(featured),
    queryFn: () => testimonialsApi.getPublic(featured),
    staleTime: 5 * 60_000,
  });
}

export function useCreateTestimonial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TestimonialInput) => testimonialsApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.testimonials }),
  });
}

export function useUpdateTestimonial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TestimonialInput }) => testimonialsApi.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.testimonials }),
  });
}

export function useDeleteTestimonial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => testimonialsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.testimonials }),
  });
}
